import { ClipboardEvent, CSSProperties, KeyboardEvent, useCallback, useRef, useState } from 'react'

export interface InputOTPProps {
    length?: number
    variant?: 'primary' | 'secondary'
    label?: string
    description?: string
    footerText?: string
    linkLabel?: string
    onLink?: () => void
    error?: string
    disabled?: boolean
    value?: string
    onComplete?: (code: string) => void
    onChange?: (code: string) => void
    style?: CSSProperties
}

export const InputOTP = ({
    length = 6,
    variant = 'primary',
    label,
    description,
    footerText,
    linkLabel,
    onLink,
    error,
    disabled = false,
    value: controlledValue,
    onComplete,
    onChange,
    style,
}: InputOTPProps) => {
    const [internalValues, setInternalValues] = useState<string[]>(Array(length).fill(''))
    const refs = useRef<(HTMLInputElement | null)[]>([])

    const values = controlledValue !== undefined ? controlledValue.split('').concat(Array(length).fill('')).slice(0, length) : internalValues

    const hasError = !!error
    const isSecondary = variant === 'secondary'

    const update = useCallback(
        (next: string[]) => {
            if (controlledValue === undefined) setInternalValues(next)
            onChange?.(next.join(''))
            if (next.every((v) => v !== '')) onComplete?.(next.join(''))
        },
        [controlledValue, onChange, onComplete]
    )

    const handleChange = useCallback(
        (idx: number, val: string) => {
            if (disabled) return
            const digit = val.replace(/\D/g, '').slice(-1)
            const next = [...values]
            next[idx] = digit
            update(next)
            if (digit && idx < length - 1) refs.current[idx + 1]?.focus()
        },
        [values, length, disabled, update]
    )

    const handleKeyDown = useCallback(
        (idx: number, e: KeyboardEvent) => {
            if (e.key === 'Backspace') {
                if (values[idx]) {
                    const next = [...values]
                    next[idx] = ''
                    update(next)
                } else if (idx > 0) {
                    refs.current[idx - 1]?.focus()
                }
            } else if (e.key === 'ArrowLeft' && idx > 0) {
                refs.current[idx - 1]?.focus()
            } else if (e.key === 'ArrowRight' && idx < length - 1) {
                refs.current[idx + 1]?.focus()
            }
        },
        [values, length, update]
    )

    const handlePaste = useCallback(
        (e: ClipboardEvent) => {
            e.preventDefault()
            const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
            const next = Array(length).fill('')
            for (let i = 0; i < pasted.length; i++) next[i] = pasted[i]
            update(next)
            const focusIdx = Math.min(pasted.length, length - 1)
            refs.current[focusIdx]?.focus()
        },
        [length, update]
    )

    const cellBase =
        'h-10 w-10 rounded-xl border-none p-0 text-center text-sm font-medium leading-5 outline-none transition-[box-shadow,background-color] duration-150'
    const cellCursor = disabled ? 'cursor-not-allowed' : 'cursor-text'
    const cellVariant = isSecondary
        ? `bg-default focus:bg-default-hover ${hasError ? 'ring-2 ring-danger' : 'focus:ring-2 focus:ring-accent'}`
        : `bg-surface ${disabled ? '' : 'not-focus:hover:bg-surface-secondary'} ${hasError ? 'ring-2 ring-danger' : 'shadow-field focus:ring-2 focus:ring-accent'}`
    const cellClass = `${cellBase} ${cellCursor} ${cellVariant} ${hasError ? 'text-danger' : 'text-foreground'}`

    return (
        <div className={`flex flex-col gap-1 ${disabled ? 'opacity-50' : ''}`} style={style}>
            {label && <span className="text-sm font-medium leading-5 text-foreground">{label}</span>}
            {description && <span className="text-sm font-normal leading-5 text-foreground-muted">{description}</span>}

            {/* OTP cells */}
            <div className={`flex items-center gap-2 ${label || description ? 'mt-1' : ''}`}>
                {values.map((val, i) => (
                    <input
                        key={i}
                        ref={(el) => {
                            refs.current[i] = el
                        }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={val}
                        disabled={disabled}
                        onChange={(e) => handleChange(i, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(i, e)}
                        onPaste={handlePaste}
                        className={cellClass}
                    />
                ))}
            </div>

            {/* Error */}
            {hasError && <span className="mt-1 text-sm font-normal leading-5 text-danger">{error}</span>}

            {/* Footer: "Didn't receive a code? Resend" */}
            {(footerText || linkLabel) && !hasError && (
                <div className="mt-1 flex flex-wrap items-center gap-1">
                    {footerText && <span className="text-sm font-normal leading-5 text-foreground-muted">{footerText}</span>}
                    {linkLabel && (
                        <button
                            type="button"
                            onClick={onLink}
                            className="cursor-pointer border-none bg-transparent p-0 text-sm font-medium leading-[18px] text-foreground underline underline-offset-[3px]"
                        >
                            {linkLabel}
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}
